/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

/**
 * Flyout directive options container.
 *
 * Drives the `amb-flyout` directive which turns a Bootstrap modal into a flyout with
 * sliding, history-aware nested sub-views (`.modal-view[view="..."]`).
 *
 * @link [Directive Architecture](resources/docs/technical/directive-architecture.md)
 */
export const flyoutOptions = {
  /**
   * Default options - always applied first.
   *
   * @property {string}      initialView    `view` name shown when the flyout opens / resets.
   * @property {string}      viewSelector   CSS selector matching each sub-view element.
   * @property {string}      viewAttribute  Attribute on a sub-view element holding its name.
   * @property {boolean}     moveToBody     Re-parent the flyout under `<body>` on init so
   *                                        Bootstrap's backdrop / focus-trap / z-index stacking behave.
   * @property {boolean}     resetOnHidden  Reset back to `initialView` (empty history) on `hidden.bs.modal`.
   * @property {string|null} triggerScope   Selector of an out-of-flyout container whose `[data-view]`
   *                                        clicks should open this flyout and switch to that view.
   * @property {Record<string, string>} triggers
   *   `data-view` value -> CSS selector of an existing page CTA. When such a `[data-view]` is clicked
   *   the CTA is proxy-clicked instead of swapping views (the flyout hides first when the click came
   *   from inside it). Use this to reuse on-page actions from within flyout views.
   *
   * View-change hooks (both optional, both receive `(detail, element)` where `detail` is
   * `{ view, previousView, history, direction }`):
   * @property {Function} [onViewChange]  Fired BEFORE the slide swap (and on open) once the change is
   *                                      not cancelled - mirrors the `amb:modal-view-change` event.
   *                                      Use it to sync view-dependent chrome before the new view slides in.
   * @property {Function} [onViewChanged] Fired AFTER the slide transition settles (and on open) -
   *                                      mirrors the `amb:modal-view-changed` event.
   */
  default: {
    initialView: 'main-menu',
    viewSelector: '.modal-view',
    viewAttribute: 'view',
    moveToBody: true,
    resetOnHidden: true,
    triggerScope: null,
    triggers: {}
  },

  /**
   * Preset for the PDP configurator flyout.
   *
   * Migrated from the legacy `navigation.helpers.viewTriggerers`: these `data-view` values map to
   * existing CTAs elsewhere on the page that open their own standalone modals / flows.
   */
  configurator: {
    triggerScope: '.configurator-navigation',
    triggers: {
      // 'view-brochure': '.download-brochure-modal__trigger',
      // 'share-with-customer' trigger removed: menu items use data-view="share-build" directly
      // (the in-flyout share view), so no CTA proxy-click is needed.
      'order-online': '#configurator-footer-actions-summary',
      'cancel-modify-order': '.modify-order-cancel-button:first'
    }
  },

  /**
   * Preset for the Product Tile promotions flyout.
   *
   * Keeps the shared footer "Build and Price" CTA in sync with the active offer view: each
   * `.modal-view` carries a `data-product-url` (the applicable-variant deep link for that
   * promotion) and the footer CTA carries a `data-default-url` (the product-level fallback).
   */
  promotions: {
    triggers: {
      'build-and-price': (event, element) => {
        const button = event.target.closest('a[data-view]');
        const hasUrl = button?.getAttribute('href') !== '#build-your-honda';

        if (!hasUrl) {
          event.preventDefault();

          $(element)
            .one('hidden.bs.modal', () => $('#selectCarModal').modal('show'))
            .modal('hide')
          ;
        }
      }
    },

    /**
     * @param {{ view: string|null }} detail The view-change detail dispatched by the directive.
     * @param {HTMLElement}           element The flyout root.
     *
     * @returns {void}
     */
    onViewChange(detail, element) {
      if (!element) {
        return;
      }

      const buildAndPriceBtn = element.querySelector('.modal-footer a');

      if (!buildAndPriceBtn) {
        return;
      }

      const activeView = detail && detail.view ? element.querySelector(`.modal-view[view="${detail.view}"]`) : null;
      const url = (activeView && activeView.dataset.productUrl) || buildAndPriceBtn.dataset.defaultUrl;

      if (url) {
        buildAndPriceBtn.setAttribute('href', url);
      }
    }
  }
};
